import { Observable } from "rxjs";
import { ActivityWithTime, ExperienceData, HotelData, MuseumData, NccData, Overnight, OvernightSummary } from ".";
import { AuditData } from "./common.model";

export interface TripCreationRequest {
    customerName: string;
    customerSurname: string;
    destination: string;
    startDate: string;
    endDate: string;
    partecipantsTotal: number;
    partecipantsAdults: number;
    partecipantsChildren: number;
    tripState: string;
    totalCost: number;
    tdMargin: number;
    totalPrice: number;
    details: Details;
    agencyMarginId: number | null;
    advisorsFeeMarginId: number | null;
    agencyMarginEconomicId: number | null;
    advisorsFeeMarginEconomicId: number | null;
}

export interface TripEditRequest {
    id: number;
    customerName: string;
    customerSurname: string;
    destination: string;
    startDate: string;
    endDate: string;
    partecipantsTotal: number;
    partecipantsAdults: number;
    partecipantsChildren: number;
    tripState: string;
    totalCost: number;
    tdMargin: number;
    totalPrice: number;
    details: Details;
    outputFileUrl?: string;
    agencyMarginId: number | null;
    advisorsFeeMarginId: number | null;
    agencyMarginEconomicId: number | null;
    advisorsFeeMarginEconomicId: number | null;
}

export interface TripSearchFilter {
    tripState?: string[];
    ownerUser?: string;
    customerName?: string;
    customerSurname?: string;
    startDateFrom?: string;
    startDateTo?: string;
    endDateFrom?: string;
    endDateTo?: string;
    destination?: string;
}

export interface TripSearchResponse {
    count: number;
    data: TripData[];
}

/**
 * This interface represent the version history of a trip. It contains all the same fields of the TripData.
 * It has also the following properties:
 * 
 * - tripId: the id of the trip. (this field should be the same as the id field in TripData)
 * - versionNumber: the version number of the trip.
 * - isInitial: a boolean indicating if this version is the first (PAID_BY_CUSTOMER) version of the trip.
 * - changedBy: the user who made the change.
 * - changedAt: the date and time when the change was made.
 * - changes: a set of records of the changes made, where each key is a field name and the value is an object containing the old and new values.
 */
export interface TripVersionHistoryData extends TripData {
    tripId: number;
    versionNumber: number;
    isInitial: boolean;
    changedBy: string;
    changedAt: Date;
    changes: Record<string, FieldChange>;
}

/**
 * Represents what changed in a specific field between versionsof trip
 */
export interface FieldChange {
    old: any
    new: any
}
export interface TripStateChangeRequest {
    state: string;
}

export interface TripData extends AuditData {
    id: number;
    customerName: string;
    customerSurname: string;
    destination: string;
    startDate: string;
    endDate: string;
    partecipantsTotal: number;
    partecipantsAdults: number;
    partecipantsChildren: number;
    tripState: string;
    totalCost: number;
    tdMargin: number;
    totalPrice: number;
    details: Details;
    outputFileUrl?: string;
    agencyMarginId: number | null;
    advisorsFeeMarginId: number | null;
    agencyMarginEconomicId: number | null;
    advisorsFeeMarginEconomicId: number | null;
}
/**
 * It represents the details of a trip, It is stored as a JSON object in the database.
 * It contains the following fields:
 * - days - an array of TripDay objects representing the individual days of the trip.
 * - overnights - an array of Overnight objects representing the overnight stays during the trip.
 */
export interface Details {
    days: TripDay[]
    overnights: Overnight[];
}

/**
 * This interface represents a single day of the trip. It has the following properties:
 * 
 * - The date can be a Date object or a string in ISO format.
 * - The description of the day (optional).
 * - The overnight summary that contains some information about the overnight stay.
 * - The list of all the Activities of the day.
 * - The list of file URLs associated with the day.
 */
export interface TripDay {
    date: Date | string;
    description?: string;
    overnightSummary?: OvernightSummary;
    activities: ActivityWithTime[];
    fileUrls: DayFile[];
}

/**
 * This interface represent a file withe the file url and the label that will be used to display it in the output html.
 */
export interface DayFile {
    url: string;
    label: string;
}

/**
 * Interface for normalized entities used in trip output generation.
 * For each entity type, it holds a dictionary mapping entity IDs to their respective data objects.
 */
export interface TripOutputEntities {
    hotels: { [id: string]: HotelData };
    experiences: { [id: string]: ExperienceData };
    nccServices: { [id: string]: NccData };
    museums: { [id: string]: MuseumData };
}

/**
 * Generic interface for normalized entities by type
 */
export interface NormalizedEntities<T> {
    [id: string]: T;
}

/**
 * This type a function for the effect that should be triggered in the transition change
 */
export type SideEffectFn = (tripId: number) => Observable<any>;

/**
 * This interface represent a trip state transition side effects definition.
 */
export interface StateSideEffects {
    mode: 'sequential' | 'parallel';        // run order
    effects: SideEffectFn[];
}


/**
 * TripState indicates the state of the trip. It can have the following values:
 * 
 * DRAFT: the initial status of the trip, which allows free modification of the information (it is necessary to enter at least one day to be able to save it).
 *
 * PAID_BY_CUSTOMER: as soon as the customer PAYS (the deposit) for the trip, it will go into this status, in fact there will be a "CONFIRM PAYMENT TRIP" button (with an appropriate confirmation popup). A trip in PAID_BY_CUSTOMER status can still be subject to modifications.
 * 
 * APPROVED_BY_ACCOUNTAT: this status is reached when the Accountant confirms all payments made to suppliers (regardless of whether there are some that have not yet been paid, e.g. "tasting").
 *
 * EXPIRED: only the super admin can change the trip on this status. From the trip budget, after that all checks have been performed, the SUPER_ADMIN can  change the trip state from APPROVED_BY_ACCOUNTANT to EXPIRED.
 *
 * CANCELLED: a trip already PAID_BY_CUSTOMER that has been cancelled. It can only be reached from PAID_BY_CUSTOMER and it can only go back to PAID_BY_CUSTOMER
 * (ACCOUNTANT and SUPER_ADMIN from the trip search). Only the SUPER_ADMIN can edit a trip on this status.
 * 
 */
export enum TripState {
    DRAFT = "DRAFT",
    PAID_BY_CUSTOMER = "PAID_BY_CUSTOMER",
    APPROVED_BY_ACCOUNTANT = "APPROVED_BY_ACCOUNTANT",
    EXPIRED = "EXPIRED",
    CANCELLED = "CANCELLED"
}

/**
 * This map is used to translate the TripState enum into a human-readable string.
 */
export const TripStateTranslationMap: Record<TripState, string> = {
    [TripState.DRAFT]: 'trip-state.draft',
    [TripState.PAID_BY_CUSTOMER]: 'trip-state.paid-by-customer',
    [TripState.APPROVED_BY_ACCOUNTANT]: 'trip-state.approved-by-accountant',
    [TripState.EXPIRED]: 'trip-state.expired',
    [TripState.CANCELLED]: 'trip-state.cancelled',
};

/**
 * This function convert a TripVersionHistoryData into a TripData by omitting the fields related to the versioning.
 * 
 * @param tripVersionHistory The TripVersionHistoryData to convert.
 * @returns The converted TripData.
 */
export function toTrip(tripVersionHistory: TripVersionHistoryData): TripData {
    // Destructure and omit the extra fields
    const { versionNumber, tripId, isInitial, changedAt, changedBy, changes, ...trip } = tripVersionHistory;
    /*
     * setting the id field with the tripId because the id
     * of the tripVersionHistory is not the REAL id of the trip, but
     * the primary key of the trip version history table.
     */
    trip.id = tripId;
    return trip;
}